import type { Device } from 'src/types/Device';
import type { DeviceActionScript } from 'src/types/ScenesActionsTypes';
import type { Script, ScriptsRequest } from 'src/types/requests/settings/scripts';
import { createGuiDeviceId } from 'src/components/Scenes/helpers/utilities_ui_to_scenes';
import { real_id } from 'src/helpers/interface_helpers';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { devices_settings } from 'src/stores';

export const RUN_SCRIPT_UI_NAME = 'run_script';

export const RUN_SCRIPT_SCENE_SET_VALUES = ['start', 'stop', 'enable', 'disable'] as const;

export type RunScriptCommand = (typeof RUN_SCRIPT_SCENE_SET_VALUES)[number];

/** Legacy import: dev:{id}:scriptstart|scriptstop with numeric set = script id. */
const LEGACY_SCRIPT_SCENE_COMMAND_START = 'scriptstart';
const LEGACY_SCRIPT_SCENE_COMMAND_STOP = 'scriptstop';

/**
 * IoTS scene-service contract: dev:{decimalId}:script{scriptId}, set = start | stop | enable | disable.
 */
export const buildScriptSceneResourceKey = (scriptId: number): string => `script${scriptId}`;

export const parseScriptIdFromSceneResource = (resource: string): number | null => {
	const match = resource.match(/^script(\d+)$/);
	if (!match) {
		return null;
	}
	const scriptId = Number(match[1]);
	return Number.isFinite(scriptId) ? scriptId : null;
};

export const isRunScriptSceneSetValue = (value: unknown): value is RunScriptCommand =>
	typeof value === 'string' && (RUN_SCRIPT_SCENE_SET_VALUES as readonly string[]).includes(value);

const isLegacyScriptSceneCommand = (resource: string): boolean =>
	resource === LEGACY_SCRIPT_SCENE_COMMAND_START || resource === LEGACY_SCRIPT_SCENE_COMMAND_STOP;

export const deviceSupportsScripts = (device: Device | undefined): boolean =>
	Boolean(device) && Number(device.gen) >= 2;

/** Scripts cached under `script:{id}` in devices_settings (same keys as device settings UI). */
export const listScriptsFromDeviceSettings = (
	settings: Record<string, unknown> | undefined,
): Script[] => {
	if (!settings) {
		return [];
	}

	const scripts: Script[] = [];
	for (const [key, raw] of Object.entries(settings)) {
		if (!key.startsWith('script:')) {
			continue;
		}
		const entry = raw as { id?: number; name?: string; enable?: boolean; running?: boolean };
		const idFromKey = Number(key.slice('script:'.length));
		const id = Number.isFinite(Number(entry?.id)) ? Number(entry.id) : idFromKey;
		if (!Number.isFinite(id)) {
			continue;
		}
		scripts.push({
			id,
			name: entry?.name || `script_${id}`,
			enable: Boolean(entry?.enable),
			running: Boolean(entry?.running),
		});
	}

	return scripts.sort((a, b) => a.id - b.id);
};

export const mergeScriptLists = (primary: Script[], fallback: Script[]): Script[] => {
	const byId = new Map<number, Script>();
	for (const script of fallback) {
		byId.set(script.id, script);
	}
	for (const script of primary) {
		const existing = byId.get(script.id);
		byId.set(script.id, existing ? { ...existing, ...script } : script);
	}
	return [...byId.values()].sort((a, b) => a.id - b.id);
};

const ROOM_SCRIPTS_PREFETCH_CONCURRENCY = 5;

const sessionScriptListCache = new Map<string, Script[]>();
const loadDeviceScriptsInFlight = new Map<string, Promise<Script[]>>();

export type LoadDeviceScriptsOptions = {
	/** When false, only updates session cache (avoids store churn during room prefetch). Default true. */
	persistSettings?: boolean;
};

/** Availability keyed by `real_id(device.id)`. */
export type RoomScriptsAvailability = Record<string, boolean>;

export type ScriptSelectOption = { value: string; title: string };

export type ScriptSettingsProbe = 'has_scripts' | 'unknown';

export const getDeviceRealId = (device: Device): string => real_id(device.id);

export const getDeviceChannelFromKey = (deviceKey: string): number => {
	const channelMatch = deviceKey.match(/_(\d+)$/);
	return channelMatch ? Number(channelMatch[1]) : 0;
};

export const getScriptSettingsProbe = (
	settings: Record<string, unknown> | undefined,
): ScriptSettingsProbe =>
	listScriptsFromDeviceSettings(settings).length > 0 ? 'has_scripts' : 'unknown';

export const syncScriptListToDeviceSettings = (deviceRealId: string, scripts: Script[]): void => {
	if (scripts.length === 0) {
		return;
	}

	const existing = (devices_settings.getByRealId(deviceRealId) || {}) as Record<string, unknown>;
	const next: Record<string, unknown> = { ...existing };

	for (const key of Object.keys(next)) {
		if (key.startsWith('script:')) {
			delete next[key];
		}
	}

	for (const script of scripts) {
		next[`script:${script.id}`] = {
			id: script.id,
			name: script.name || `script_${script.id}`,
			enable: Boolean(script.enable),
			running: Boolean(script.running),
		};
	}

	devices_settings.update_devices_settings(deviceRealId, next);
};

export const getCachedDeviceScripts = (deviceRealId: string): Script[] | undefined =>
	sessionScriptListCache.get(deviceRealId);

export const setCachedDeviceScripts = (deviceRealId: string, scripts: Script[]): void => {
	sessionScriptListCache.set(deviceRealId, scripts);
};

export const mapScriptsToSelectOptions = (
	scripts: Script[],
	settings: Record<string, unknown> | undefined,
): ScriptSelectOption[] =>
	scripts.map((script) => ({
		value: String(script.id),
		title:
			(settings?.[`script:${script.id}`] as { name?: string } | undefined)?.name ||
			script.name ||
			`Script ${script.id}`,
	}));

const loadDeviceScriptsInternal = async (
	device: Device,
	devicesSettings: Record<string, unknown>,
	options: LoadDeviceScriptsOptions = {},
): Promise<Script[]> => {
	const deviceRealId = getDeviceRealId(device);
	if (sessionScriptListCache.has(deviceRealId)) {
		return sessionScriptListCache.get(deviceRealId) || [];
	}

	const settings = (devicesSettings[deviceRealId] || {}) as Record<string, unknown>;
	const probe = getScriptSettingsProbe(settings);

	if (probe === 'has_scripts') {
		const scripts = listScriptsFromDeviceSettings(settings);
		setCachedDeviceScripts(deviceRealId, scripts);
		return scripts;
	}

	const res: ScriptsRequest = await gen2Request(device, 'Script.List', {});
	if (!res?.isok) {
		return listScriptsFromDeviceSettings(settings);
	}

	const apiScripts = ((res.data?.scripts || res.scripts) as Script[]) || [];
	const scripts = mergeScriptLists(apiScripts, listScriptsFromDeviceSettings(settings));
	setCachedDeviceScripts(deviceRealId, scripts);

	if (options.persistSettings !== false) {
		syncScriptListToDeviceSettings(deviceRealId, scripts);
	}

	return scripts;
};

export const loadDeviceScripts = async (
	device: Device,
	devicesSettings: Record<string, unknown>,
	options: LoadDeviceScriptsOptions = {},
): Promise<Script[]> => {
	const deviceRealId = getDeviceRealId(device);
	const inFlight = loadDeviceScriptsInFlight.get(deviceRealId);
	if (inFlight) {
		return inFlight;
	}

	const loadPromise = loadDeviceScriptsInternal(device, devicesSettings, options).finally(() => {
		if (loadDeviceScriptsInFlight.get(deviceRealId) === loadPromise) {
			loadDeviceScriptsInFlight.delete(deviceRealId);
		}
	});

	loadDeviceScriptsInFlight.set(deviceRealId, loadPromise);
	return loadPromise;
};

export const deviceHasScriptsFromAvailability = (
	availability: RoomScriptsAvailability,
	device: Device,
): boolean => availability[getDeviceRealId(device)] === true;

export const deviceHasNoScriptsFromAvailability = (
	availability: RoomScriptsAvailability,
	device: Device,
): boolean => availability[getDeviceRealId(device)] === false;

const groupDeviceKeysByRealId = (
	deviceKeys: string[],
	devices: Record<string, Device>,
): { deviceByRealId: Map<string, Device> } => {
	const deviceByRealId = new Map<string, Device>();

	for (const deviceKey of deviceKeys) {
		const device = devices[deviceKey];
		if (!device || !deviceSupportsScripts(device)) {
			continue;
		}
		const deviceRealId = getDeviceRealId(device);
		if (!deviceByRealId.has(deviceRealId)) {
			deviceByRealId.set(deviceRealId, device);
		}
	}

	return { deviceByRealId };
};

export const resolveRoomScriptsAvailabilitySync = (
	deviceKeys: string[],
	devices: Record<string, Device>,
	devicesSettings: Record<string, unknown>,
): {
	availability: RoomScriptsAvailability;
	unknownRealIds: string[];
	deviceByRealId: Map<string, Device>;
} => {
	const { deviceByRealId } = groupDeviceKeysByRealId(deviceKeys, devices);
	const availability: RoomScriptsAvailability = {};
	const unknownRealIds: string[] = [];

	for (const [deviceRealId, device] of deviceByRealId) {
		if (sessionScriptListCache.has(deviceRealId)) {
			const sessionScripts = sessionScriptListCache.get(deviceRealId) || [];
			availability[deviceRealId] = sessionScripts.length > 0;
			continue;
		}

		const settings = (devicesSettings[deviceRealId] || {}) as Record<string, unknown>;
		const probe = getScriptSettingsProbe(settings);

		if (probe === 'has_scripts') {
			availability[deviceRealId] = true;
			continue;
		}

		unknownRealIds.push(deviceRealId);
	}

	return { availability, unknownRealIds, deviceByRealId };
};

export const fetchUnknownRoomScriptsAvailability = async (
	unknownRealIds: string[],
	deviceByRealId: Map<string, Device>,
	devicesSettings: Record<string, unknown>,
	options: {
		abortSeq: number;
		getCurrentAbortSeq: () => number;
	},
): Promise<RoomScriptsAvailability> => {
	const availability: RoomScriptsAvailability = {};

	const resolveRealId = async (deviceRealId: string) => {
		if (options.getCurrentAbortSeq() !== options.abortSeq) {
			return;
		}

		const device = deviceByRealId.get(deviceRealId);
		if (!device) {
			return;
		}

		const scripts = await loadDeviceScripts(device, devicesSettings, {
			persistSettings: false,
		});

		if (options.getCurrentAbortSeq() !== options.abortSeq) {
			return;
		}

		availability[deviceRealId] = scripts.length > 0;
	};

	for (let index = 0; index < unknownRealIds.length; index += ROOM_SCRIPTS_PREFETCH_CONCURRENCY) {
		if (options.getCurrentAbortSeq() !== options.abortSeq) {
			return availability;
		}

		const batch = unknownRealIds.slice(index, index + ROOM_SCRIPTS_PREFETCH_CONCURRENCY);
		await Promise.all(batch.map(resolveRealId));
	}

	return availability;
};

export const prefetchRoomDeviceScripts = async (
	deviceKeys: string[],
	devices: Record<string, Device>,
	devicesSettings: Record<string, unknown>,
	options: {
		abortSeq: number;
		getCurrentAbortSeq: () => number;
	},
): Promise<RoomScriptsAvailability> => {
	const syncResult = resolveRoomScriptsAvailabilitySync(deviceKeys, devices, devicesSettings);

	if (syncResult.unknownRealIds.length === 0) {
		return syncResult.availability;
	}

	const fetched = await fetchUnknownRoomScriptsAvailability(
		syncResult.unknownRealIds,
		syncResult.deviceByRealId,
		devicesSettings,
		options,
	);

	return { ...syncResult.availability, ...fetched };
};

export const buildRunScriptSceneAction = (
	hexToDecimal: string | number,
	guiDeviceId: string,
	scriptId: number,
	command: RunScriptCommand = 'start',
): DeviceActionScript => ({
	r: `dev:${hexToDecimal}:${buildScriptSceneResourceKey(scriptId)}`,
	set: command,
	_gui_device_id: guiDeviceId,
	_gui_purpose: 'action',
	_gui_type: RUN_SCRIPT_UI_NAME,
});

export const buildRunScriptSceneActionFromDevice = (
	hexToDecimal: string | number,
	deviceId: string,
	channel: number,
	scriptId: number,
	command: RunScriptCommand = 'start',
): DeviceActionScript =>
	buildRunScriptSceneAction(
		hexToDecimal,
		createGuiDeviceId({ id: deviceId, channel }),
		scriptId,
		command,
	);

export type RunScriptFlowCardData = {
	deviceRealId: string;
	deviceId: string;
	deviceName?: string;
	roomId?: string;
	roomName?: string;
	channel: number;
	scriptId: number;
	scriptName?: string;
	command: RunScriptCommand;
};

export const buildRunScriptFlowCardData = (
	device: Device,
	scriptId: number,
	options: {
		channel?: number;
		command?: RunScriptCommand;
		scriptName?: string;
		roomId?: string;
		roomName?: string;
	} = {},
): RunScriptFlowCardData => {
	const channel = Number(options.channel ?? 0);
	const deviceRealId = real_id(device.id);
	const deviceId = channel > 0 ? `${deviceRealId}_${channel}` : deviceRealId;

	return {
		deviceRealId,
		deviceId,
		deviceName: device.name,
		roomId: options.roomId ?? String(device.room_id ?? ''),
		roomName: options.roomName,
		channel: Number.isFinite(channel) ? channel : 0,
		scriptId,
		scriptName: options.scriptName,
		command: options.command ?? 'start',
	};
};

export const buildRunScriptSceneActionFromFlowData = (
	data: RunScriptFlowCardData,
	hexToDecimal: string | number,
): DeviceActionScript =>
	buildRunScriptSceneAction(
		hexToDecimal,
		data.deviceId || createGuiDeviceId({ id: data.deviceRealId, channel: data.channel ?? 0 }),
		data.scriptId,
		data.command ?? 'start',
	);

export const parseRunScriptFromSceneAction = (
	action: DeviceActionScript,
): RunScriptFlowCardData | null => {
	if (!action?.r || typeof action.set === 'undefined') {
		return null;
	}

	const parts = action.r.split(':');
	if (parts.length < 3 || parts[0] !== 'dev') {
		return null;
	}

	const resource = parts[2];
	const guiDeviceId = action._gui_device_id ?? '';
	const channelMatch = guiDeviceId.match(/_(\d+)$/);
	const deviceRealId = channelMatch ? guiDeviceId.replace(/_\d+$/, '') : guiDeviceId;
	const channel = channelMatch ? Number(channelMatch[1]) : 0;

	const scriptIdFromResource = parseScriptIdFromSceneResource(resource);
	if (scriptIdFromResource !== null && isRunScriptSceneSetValue(action.set)) {
		return {
			deviceRealId,
			deviceId: guiDeviceId || deviceRealId,
			channel: Number.isFinite(channel) ? channel : 0,
			scriptId: scriptIdFromResource,
			command: action.set,
		};
	}

	if (isLegacyScriptSceneCommand(resource)) {
		const scriptId = Number(action.set);
		if (!Number.isFinite(scriptId)) {
			return null;
		}
		return {
			deviceRealId,
			deviceId: guiDeviceId || deviceRealId,
			channel: Number.isFinite(channel) ? channel : 0,
			scriptId,
			command: resource === LEGACY_SCRIPT_SCENE_COMMAND_STOP ? 'stop' : 'start',
		};
	}

	return null;
};
